import { AppStateEnum } from '../../../main/types/app-state.types.ts';
import { getAudioRecordingUtil } from '../../../main/utils/audio/audio-recording.util.ts';
import { getLocaleUtil } from '../../../main/utils/locale.util.ts';
import { getSentryUtil } from '../../../main/utils/monitoring/sentry.util.ts';
import { getReduxStoreUtil } from '../../../main/utils/redux-store.util.ts';

import { DEFAULT_SENTENCE_GRADING_FEEDBACK } from '../../constants/sentence-grading-feedback';
import { getPronunciationGraderParametersQueryRequest } from '../../requests/get-pronunciation-grader-parameters.query.ts';
import { updateAppStateThunk } from '../../slices/app-state/thunks/update-app-state.thunk.ts';
import {
  PronunciationGrader,
  PronunciationGraderEntry,
  PronunciationGraderState,
  PronunciationGradingResult,
  SentencePronunciationGraderResultDetails,
} from '../../types/pronunciation-grader.types.ts';
import { GraderType } from '../../types/pronunciation-grading-parameters.types.ts';
import { SentenceParameters, SentenceParametersSchema } from '../../types/pronunciation-grading-parameters.types.ts';
import { SentenceGradingFeedback } from '../../types/sentence-grading-feedback.types.ts';
import { SpeechSuperIntermediateAssessmentResponse } from '../../types/speechsuper-response.types.ts';

import { SpeechSuperGradingUtil } from '../speechsuper/speechsuper-grading.util.ts';

/**
 * Handles grading of sentence pronunciation.
 * Uses SpeechSuper for real-time grading of sentence pronunciation.
 * Returns early when the first incorrect word is detected.
 */
export class SentencePronunciationGrader implements PronunciationGrader<SentencePronunciationGraderResultDetails> {
  private static _instance: SentencePronunciationGrader;

  static get instance(): SentencePronunciationGrader {
    if (!SentencePronunciationGrader._instance) {
      SentencePronunciationGrader._instance = new SentencePronunciationGrader();
    }
    return SentencePronunciationGrader._instance;
  }

  private constructor(private audioRecorder = getAudioRecordingUtil()) {}

  private entry: PronunciationGraderEntry<SentenceParameters> | null = null;

  private state: PronunciationGraderState = {
    isInitialized: false,
    isRecording: false,
    isGrading: false,
  };

  /**
   * Initializes the grader with the reference text to check against.
   * Must be called before starting grading.
   * @param refText - The reference text to grade against
   */
  async initialize(refText: string): Promise<void> {
    if (this.state.isRecording || this.state.isGrading) {
      throw new Error(getLocaleUtil().select('learn:grader_busy'));
    }

    // Get grading parameters
    const result = await getPronunciationGraderParametersQueryRequest({
      graderType: GraderType.SENTENCE,
      refText,
    });

    if (!result.success || !result.data) {
      throw new Error(getLocaleUtil().select('learn:grader_initialization_failed'));
    }

    const parameters = SentenceParametersSchema.parse(result.data.parameters);

    // Initialize grader for sentence
    const grader = new SpeechSuperGradingUtil();
    grader.initialize({
      graderType: GraderType.SENTENCE,
      refText: parameters.sentence,
    });

    this.entry = {
      grader,
      parameters,
      isCorrect: true,
    };

    this.state.isInitialized = true;
  }

  /**
   * Starts the grading process.
   * Must be called after initialize.
   * Returns early when the first incorrect word is detected.
   * @returns A promise that resolves with the grading result and first incorrect word if any
   */
  async startGrading(
    sentenceGradingFeedback: SentenceGradingFeedback = DEFAULT_SENTENCE_GRADING_FEEDBACK
  ): Promise<PronunciationGradingResult<SentencePronunciationGraderResultDetails>> {
    return new Promise((resolve, reject) => {
      if (!this.state.isInitialized || !this.entry) {
        throw new Error(getLocaleUtil().select('learn:grader_not_initialized'));
      }

      if (this.state.isRecording || this.state.isGrading) {
        throw new Error(getLocaleUtil().select('learn:grader_busy'));
      }
      // Start the grader
      this.entry.grader.startGrading({
        callbacks: {
          onIntermediateResult: (result: SpeechSuperIntermediateAssessmentResponse) => {
            // If we already cleaned up, don't do anything
            if (!this.entry) return;

            if (sentenceGradingFeedback === SentenceGradingFeedback.IMMEDIATE) {
              // If we detect an incorrect word, stop recording early
              const incorrectWordIndex = result.result.words.findIndex(
                (word) => !this.isWordCorrect(word.scores.pronunciation)
              );
              if (incorrectWordIndex !== -1) {
                const incorrectWord = result.result.words[incorrectWordIndex];
                const recognizedText = result.result.words
                  .slice(0, incorrectWordIndex)
                  .map((word) => word.word)
                  .join(' ');

                resolve({
                  isCorrect: false,
                  details: {
                    firstIncorrectWord: incorrectWord.word,
                    recognizedText,
                  },
                });
                this.stopAndCleanup();
                return;
              }
            }
            const isCompletelyRead = this.isFullyRead(result.result.words.map((word) => word.word));
            if (isCompletelyRead) {
              const recognizedText = result.result.words.map((word) => word.word).join(' ');
              resolve({ isCorrect: true, details: { firstIncorrectWord: '', recognizedText } });
              this.stopAndCleanup();
              return;
            }
          },
        },
      });
      this.state.isGrading = true;

      // Start recording with real-time processing
      this.audioRecorder
        .record({
          callbacks: {
            onPartialResult: async (audioData) => {
              await this.entry?.grader.sendAudioData(audioData);
            },
          },
          silenceDetection: {
            enabled: true,
            onSilenceDetected: async () => {
              const result = await this.finalizeGrading();
              resolve(result);
            },
          },
        })
        .catch(reject);
    });
  }

  /**
   * Aborts the grading process.
   * @returns A promise that resolves when the grading is aborted
   */
  async abort(): Promise<void> {
    const isRecording = this.state.isRecording;
    const isGrading = this.state.isGrading;
    const grader = this.entry?.grader;
    this.cleanup();

    if (isRecording) {
      await this.audioRecorder.stopRecording();
    }
    if (isGrading && grader) {
      await grader.abort();
    }
  }

  private async finalizeGrading(): Promise<PronunciationGradingResult<SentencePronunciationGraderResultDetails>> {
    try {
      // If we already cleaned up, don't do anything and return false
      if (!this.entry) return { isCorrect: false };

      const dispatch = await getReduxStoreUtil().getDispatch();
      await dispatch(updateAppStateThunk({ state: AppStateEnum.Evaluating }));

      // Get results from grader
      const result = await this.entry?.grader.stopGrading();
      if (!result?.result.words) {
        return { isCorrect: false };
      }

      // Find first incorrect word if any
      const incorrectWord = result.result.words.find((word) => !this.isWordCorrect(word.scores.pronunciation));
      const isCorrect = !incorrectWord;
      const recognizedText = result.result.words.map((word) => word.word).join(' ');

      return {
        isCorrect,
        details: incorrectWord ? { firstIncorrectWord: incorrectWord.word, recognizedText } : undefined,
      };
    } catch (error) {
      getSentryUtil().trackError(error as Error, {
        grader: 'sentence_pronunciation',
        refText: this.entry?.parameters.sentence ?? undefined,
      });
      return { isCorrect: false };
    } finally {
      this.cleanup();
    }
  }

  private isWordCorrect(score: number): boolean {
    return score >= this.entry!.parameters.threshold;
  }

  private isFullyRead(words: string[]): boolean {
    return words.join(' ').length === this.entry?.parameters.sentence.length;
  }

  private stopAndCleanup(): void {
    this.audioRecorder.stopRecording();
    this.cleanup();
  }

  private cleanup(): void {
    this.state.isRecording = false;
    this.state.isGrading = false;
    this.state.isInitialized = false;
    this.entry = null;
  }
}

export const getSentencePronunciationGrader = (): PronunciationGrader<SentencePronunciationGraderResultDetails> =>
  SentencePronunciationGrader.instance;
